/* [PATCH dc-skupina-fe v1] Delovni čas → Skupinski pregled dela (odločitev 83, 101–104): Pregled dela vseh izbranih baz
   s stolpcem Podjetje. Izbira baz: Nastavitve DČ (kartica »Skupinski pregled dela«, nastavitev uporabnika).
   Klik na vrstico preklopi izbrano podjetje in odpre Moj mesec zaposlenega; zaklep = vse izbrane baze; izvoz = en ZIP.
   API: /dc-skupina/pregled-dela, /dc-skupina/pregled-dela/zip, /dc-skupina/zakleni (backend api/dc_skupina.py). */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader, CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { ZaklepOkno } from '../components/ZaklepOkno'; // [PATCH dc-zaklep-okno v1]

const napaka = (e: any) => e?.response?.data?.detail
  ? (typeof e.response.data.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response.data.detail))
  : (e?.message || 'Napaka');
const kartica = 'bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700';
const gumb = 'px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2';
const gumb2 = 'px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700 hover:border-blue-400 bg-white dark:bg-gray-900';
const MESECI = ['Januar', 'Februar', 'Marec', 'April', 'Maj', 'Junij', 'Julij', 'Avgust', 'September', 'Oktober', 'November', 'December'];
const st = (s: any) => Number(s || 0).toLocaleString('sl-SI', { maximumFractionDigits: 2 });
const eur = (s: any) => Number(s || 0).toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const STOLPCEV = 16;

export function DcSkupinaPage() {
  const { setSelectedCompany } = useAuthStore() as any;
  const nav = useNavigate();
  const danes = new Date();
  const [lm, setLm] = useState(() => { const m = danes.getMonth(); /* privzeto pretekli mesec (plače) */
    return m === 0 ? { leto: danes.getFullYear() - 1, mesec: 12 } : { leto: danes.getFullYear(), mesec: m }; });
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const nalozi = useCallback(() => {
    setErr(null); setD(null);
    api.get('/dc-skupina/pregled-dela', { params: { leto: lm.leto, mesec: lm.mesec } })
      .then((r: any) => setD(r.data)).catch((e: any) => setErr(napaka(e)));
  }, [lm]);
  useEffect(() => { nalozi(); }, [nalozi]);

  const premik = (k: number) => setLm(s => { let m = s.mesec + k, l = s.leto; if (m < 1) { m = 12; l--; } if (m > 12) { m = 1; l++; } return { leto: l, mesec: m }; });
  const baze: any[] = d?.baze || [];
  const zap: any[] = d?.zaposleni || [];
  const nepotrjeni = zap.filter((z: any) => !z.potrjeno).length;
  const vsiZakl = zap.length > 0 && zap.every((z: any) => z.zaklenjeno);
  const mm = `${MESECI[lm.mesec - 1]} ${lm.leto}`;

  const izvozi = async () => {
    setDela(true); setErr(null);
    try {
      const x = await api.get('/dc-skupina/pregled-dela/zip', { params: { leto: lm.leto, mesec: lm.mesec }, responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(x.data);
      a.download = `Obracuni_skupina_${lm.leto}_${String(lm.mesec).padStart(2, '0')}.zip`; a.click();
    } catch (e) { setErr(napaka(e)); } finally { setDela(false); }
  };
  const [okno, setOkno] = useState<boolean | null>(null); // [PATCH dc-zaklep-okno v1] null = zaprto, true = zakleni, false = odkleni
  const zakleniVse = async (z: boolean) => {
    setOkno(null);
    setDela(true); setErr(null); setMsg(null);
    try {
      const r = await api.post('/dc-skupina/zakleni', { leto: lm.leto, mesec: lm.mesec, zakleni: z });
      const nap = (r.data?.baze || []).filter((b: any) => b.napaka);
      setMsg((z ? 'Zaklenjeno' : 'Odklenjeno') + ` v ${(r.data?.baze || []).length - nap.length} bazah` + (nap.length ? ' · napake: ' + nap.map((b: any) => `${b.ime}: ${b.napaka}`).join('; ') : ''));
      nalozi();
    } catch (e) { setErr(napaka(e)); } finally { setDela(false); }
  };
  const odpri = async (z: any) => { /* preklop baze (odločitev 102) */
    let c: any = null;
    try {
      const r = await api.get('/companies');
      const sez: any[] = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.companies || []);
      c = sez.find((x: any) => String(x.id) === String(z.podjetje_id)) || null;
    } catch { c = null; }
    setSelectedCompany(c || { id: z.podjetje_id, name: z.podjetje });
    nav(`/moj-mesec?zaposleni=${z.id}&leto=${lm.leto}&mesec=${lm.mesec}`);
  };

  return (
    <div className="p-4 md:p-6 space-y-4 w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold">Skupinski pregled dela</h1>
          <div className="text-sm text-gray-500">Kaj gre v plače (Vasco) za izbrani mesec — vse izbrane baze</div>
        </div>
        <div className="flex items-center justify-center gap-2">
          <button type="button" className={gumb2} onClick={() => premik(-1)}><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-base font-semibold w-44 text-center">{mm}</div>
          <button type="button" className={gumb2} onClick={() => premik(1)}><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center justify-start md:justify-end gap-2">
          {zap.length > 0 && <button type="button" className={gumb2} disabled={dela} onClick={() => setOkno(!vsiZakl)}>{vsiZakl ? 'Odkleni mesec v vseh bazah' : '🔒 Zakleni mesec v vseh bazah'}</button>}
          <button type="button" className={gumb} disabled={dela || !zap.length} onClick={izvozi}>
            {dela ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Izvoz ZIP (Vasco)</button>
        </div>
      </div>

      {err && <div className="px-3 py-2 rounded-lg border text-sm bg-red-50 text-red-700 border-red-200">{err}</div>}
      {msg && <div className="px-3 py-2 rounded-lg border text-sm bg-green-50 text-green-800 border-green-200">{msg}</div>}
      {!d && !err && <Loader className="w-5 h-5 animate-spin" />}
      {d && baze.length < 2 && <div className="px-3 py-2 rounded-lg border text-sm bg-amber-50 text-amber-800 border-amber-200">
        Izbranih je {baze.length} {baze.length === 1 ? 'baza' : 'baz'}. Baze izbereš v Nastavitvah Delovnega časa (kartica »Skupinski pregled dela«).</div>}
      {d && (
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">Baz <b>{baze.length}</b></span>
          <span className="text-gray-500 ml-auto">{zap.length} {zap.length === 1 ? 'zaposleni' : 'zaposlenih'} · potrjenih {zap.length - nepotrjeni}
            {nepotrjeni > 0 && <span className="text-amber-700"> · nepotrjenih {nepotrjeni} (izvoz jih vseeno vključi)</span>} · klik na vrstico preklopi bazo in odpre mesec zaposlenega</span>
        </div>)}

      {d && (
        <div className={kartica + ' overflow-x-auto'}>
          <table className="w-full text-sm min-w-[1200px]">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40">
              <th className="py-3 px-4">Podjetje</th><th className="py-3 px-3">Zaposleni</th><th className="py-3 px-3">Ure</th><th className="py-3 px-3">Stanje ur</th>
              <th colSpan={2} className="py-3 px-3">Redne ure</th><th colSpan={2} className="py-3 px-3">Praznik</th><th colSpan={2} className="py-3 px-3">Dopust</th>
              <th colSpan={2} className="py-3 px-3">Bolniška</th><th colSpan={2} className="py-3 px-3">Dodatki</th><th colSpan={2} className="py-3 px-3">Potni nalogi</th></tr></thead>
            <tbody>
              {baze.map((b: any) => {
                const vrst = zap.filter((z: any) => z.podjetje_id === b.id);
                const info = b.info;
                return (<React.Fragment key={b.id}>
                  <tr className="bg-gray-50/70 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700">
                    <td colSpan={STOLPCEV} className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
                      <b className="text-sm text-gray-900 dark:text-white">{b.ime}</b>
                      {b.napaka ? <span className="ml-2 text-red-600">{b.napaka}</span>
                        : <span className="ml-2">{b.zaposlenih} {b.zaposlenih === 1 ? 'zaposleni' : 'zaposlenih'}
                          {info && <> · delovne ure {st(info.delovne)} h · delovni dnevi {info.delovne_dni} · skupaj {st(info.fond)} h / {info.fond_dni} dni</>}</span>}
                    </td></tr>
                  {vrst.map((z: any) => {
                    const k: any = z.kategorije || {};
                    const s: any = z.stanje || {};
                    const razl = Number(z.razlika);
                    const seznam = (xs: any[]) => (xs && xs.length
                      ? <><td className="py-3 pl-3 pr-2 text-right whitespace-nowrap w-px font-medium">{xs.map((x: any, i: number) =>
                          <div key={i}>{x.enota === '€' ? eur(x.vrednost) : st(x.vrednost) + ' ' + x.enota}</div>)}</td>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{xs.map((x: any, i: number) =>
                          <div key={i} className="truncate max-w-[14rem]" title={x.naziv}>{x.naziv}</div>)}</td></>
                      : <><td className="py-3 pl-3 pr-2 text-gray-300">—</td><td className="py-3 pr-4" /></>);
                    return (<React.Fragment key={z.podjetje_id + z.id}>
                      <tr onClick={() => odpri(z)} className={'align-top cursor-pointer hover:bg-blue-50/60 dark:hover:bg-blue-500/10' + (z.opozorila.length ? '' : ' border-b border-gray-100 dark:border-gray-800')}>
                        <td className="py-3 px-4 text-gray-500 truncate max-w-[12rem]" title={z.podjetje}>{z.podjetje}</td>
                        <td className="py-3 px-3">
                          <div className="font-medium">{z.ime}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">{z.sifra ? 'šifra ' + z.sifra : 'brez šifre'}
                            {z.potrjeno
                              ? <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />potrjen</span>
                              : <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">ni potrjen</span>}
                            {z.zaklenjeno && <span className="px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">🔒 zaklenjen</span>}</div></td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div>{st(z.delo)} <span className="text-gray-400">/ {st(z.fond)} h</span></div>
                          <div className={'text-xs ' + (razl < 0 ? 'text-red-600' : razl > 0 ? 'text-green-600' : 'text-gray-400')}>mesec {razl > 0 ? '+' : ''}{st(z.razlika)} h</div></td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div className={'font-medium ' + (Number(s.konec) < 0 ? 'text-red-600' : Number(s.konec) > 0 ? 'text-green-600' : '')}>{Number(s.konec) > 0 ? '+' : ''}{st(s.konec)} h</div>
                          <div className="text-xs text-gray-400">prenos {Number(s.prenos) > 0 ? '+' : ''}{st(s.prenos)} h{Number(s.izplacilo) > 0 ? ` · izplačano ${st(s.izplacilo)} h` : ''}</div></td>
                        {seznam([...(k.redne || []), ...(k.nadure || [])])}
                        {seznam(k.praznik)}
                        {seznam(k.dopust)}
                        {seznam(k.bolniska)}
                        {seznam([...(k.dodatki || []), ...(k.prehrana || [])])}
                        {seznam(k.potni)}
                      </tr>
                      {z.opozorila.length > 0 && <tr onClick={() => odpri(z)} className="cursor-pointer border-b border-gray-100 dark:border-gray-800">
                        <td colSpan={STOLPCEV} className="px-4 pb-3 pt-0 text-xs text-amber-700">
                          <div className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{z.opozorila.join(' · ')}</span></div></td></tr>}
                    </React.Fragment>);
                  })}
                  {!b.napaka && vrst.length === 0 && <tr className="border-b border-gray-100 dark:border-gray-800"><td colSpan={STOLPCEV} className="py-3 px-4 text-gray-400">V tem mesecu ni zaposlenih</td></tr>}
                </React.Fragment>);
              })}
              {baze.length === 0 && <tr><td colSpan={STOLPCEV} className="py-6 text-center text-gray-400">Ni izbranih baz</td></tr>}
            </tbody>
          </table>
        </div>)}
      <ZaklepOkno odprto={okno !== null} zakleni={!!okno} mesec={mm} dela={dela} nepotrjenih={nepotrjeni}
        baze={baze.filter((b: any) => !b.napaka).map((b: any) => ({ ime: b.ime, zaposlenih: b.zaposlenih }))}
        onPotrdi={() => zakleniVse(!!okno)} onPreklici={() => setOkno(null)} />{/* [PATCH dc-zaklep-okno v1] */}
    </div>
  );
}

export default DcSkupinaPage;
